/**
 * One line under a run for what it filed in one project: the project, then
 * Undo, Move (when frames were filed) and Rename (when the project is new).
 * Denker's own sentence says what happened; the line only acts on it.
 */
import { useMemo, useState } from "react";
import { Badge, Button, InputDialog } from "@/components/ui";
import { FrameMoveToProjectDialog } from "@/components/shapes/shared/frame-move-to-project-dialog";
import { useT } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";
import { undoFiling } from "@/lib/filing-undo";
import { moveFramesToProject } from "@/lib/frame-menu-actions";
import { useProjectStore } from "@/stores/project-store";
import type { ThreadItem } from "./conversation-thread-items";

type FilingItem = Extract<ThreadItem, { kind: "filing" }>;

export function ConversationFilingNotice({
  filing,
  testId,
}: {
  filing: FilingItem;
  testId: string;
}) {
  const t = useT(framesConversationMessages);
  const { receipts, running } = filing;
  const latest = receipts[receipts.length - 1]!;
  const projectId = latest.project_id;
  const frameIds = useMemo(
    () => [
      ...new Set(
        receipts.flatMap((receipt) =>
          receipt.items
            .filter((item) => item.type === "frame")
            .map((item) => item.id),
        ),
      ),
    ],
    [receipts],
  );
  const newProject = receipts.some((receipt) => receipt.new_project);
  const [settled, setSettled] = useState<"undone" | "moved" | null>(null);
  const [movedTo, setMovedTo] = useState<string | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [dialog, setDialog] = useState<"move" | "rename" | null>(null);
  const shownProjectId = movedTo ?? projectId;
  const storedName = useProjectStore(
    (state) =>
      state.projects.find((project) => project.id === shownProjectId)?.name,
  );
  const projectName = storedName ?? latest.project_name;
  const renameProject = useProjectStore((state) => state.renameProject);
  const noticeTestId = `${testId}-filing-${filing.id}`;
  // Undo and Move wait for the run: the server refuses moving its frames.
  const afterRunHint = running ? t("filingAfterRun") : undefined;

  const undo = async () => {
    setUndoing(true);
    const undone = await undoFiling(receipts);
    setUndoing(false);
    if (undone) setSettled("undone");
  };

  const move = async (toProjectId: string) => {
    const moved = await moveFramesToProject(frameIds, toProjectId, projectId);
    if (moved) {
      setMovedTo(toProjectId);
      setSettled("moved");
    }
    return moved;
  };

  return (
    <div
      role="group"
      className="flex min-w-0 items-center gap-1 text-canvas-xs text-muted"
      aria-label={t("filedIn", { project: projectName })}
      data-testid={noticeTestId}
    >
      <Badge
        variant="outline"
        className="min-w-0 max-w-[50%]"
        data-testid={`${noticeTestId}-project`}
      >
        <span className="truncate">{projectName}</span>
      </Badge>
      {settled ? (
        <span className="px-2" data-testid={`${noticeTestId}-${settled}`}>
          {settled === "undone" ? t("filingUndone") : t("filingMoved")}
        </span>
      ) : (
        <>
          <span title={afterRunHint}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={running || undoing}
              onClick={() => void undo()}
              data-testid={`${noticeTestId}-undo`}
            >
              {t("filingUndo")}
            </Button>
          </span>
          {frameIds.length > 0 ? (
            <span title={afterRunHint}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={running || undoing}
                onClick={() => setDialog("move")}
                data-testid={`${noticeTestId}-move`}
              >
                {t("filingMove")}
              </Button>
            </span>
          ) : null}
          {newProject ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={undoing}
              onClick={() => setDialog("rename")}
              data-testid={`${noticeTestId}-rename`}
            >
              {t("filingRename")}
            </Button>
          ) : null}
        </>
      )}
      {dialog === "move" ? (
        <FrameMoveToProjectDialog
          frameIds={frameIds}
          fromProjectId={projectId}
          onMove={move}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "rename" ? (
        <InputDialog
          title={t("filingRenameTitle")}
          inputLabel={t("filingProjectName")}
          initialValue={projectName}
          maxLength={255}
          errorMessage={t("filingRenameFailed")}
          onCancel={() => setDialog(null)}
          onSubmit={async (name) => {
            await renameProject(projectId, name);
            setDialog(null);
          }}
          data-testid={`${noticeTestId}-rename-dialog`}
        />
      ) : null}
    </div>
  );
}
